<template>
  <b-modal
    ref="modalPackagingSettings"
    body-class="!p-10 text-black w-full"
    size="lg"
    centered
    hide-footer
    hide-header
    hide-header-close
    no-close-on-backdrop
  >
    <h3 class="text-[24px text-[#333333] mb-[24px] text-center font-semibold">
      Packaging
    </h3>

    <b-table
      :items="selectedPackingMaterials"
      :fields="fields"
    >
      <template #cell(action)="data">
        <b-button
          variant="ghost"
          class="!py-[2px] !px-[8px] !text-[16px] !py-[10px] !size-[35px]"
          :disabled="isRemoveButtonDisabled(data.item)"
          @click="removePackingMaterial(data.index)"
        >
          <trash-icon :disabled="isRemoveButtonDisabled(data.item)" />
        </b-button>
      </template>
      <template #cell(name)="data">
        <infinite-select
          v-model="selectedPackingMaterials[data.index]"
          placeholder="Bahan Packing"
          class="flex-1 bg-white"
          option-label="name"
          :default-value="defaultPackings[data.index]"
          :options="getMaterialOptions(data.item)"
          :has-next-page="fetcher.hasMore || false"
          :debounce-timeout="500"
          :loading="fetcher.loading || false"
          :clearable="false"
          :reduce="reducePackingMaterial"
          @on-search="handleSearchPacking"
          @on-intersect="handleIntersect"
        >
          <template #no-options="{ search, searching }">
            <template v-if="searching">
              Bahan packing untuk <em>'{{ search }}'</em> tidak ditemukan.
            </template>
            <em v-else>Masukan bahan packing yang valid</em>
          </template>
        </infinite-select>
      </template>
      <template #cell(layer)="data">
        <div class="w-full c-qty">
          <b-input-group class="w-full">
            <b-input-group-prepend>
              <b-button
                variant="outline-secondary"
                size="sm"
                class="!p-[6px] !bg-[#F8F8F8] !border-[#E2E2E2]"
                :disabled="isOnMinimumLayer(data.item)"
                @click="handleChangePackingLayer(data.item, 'minus')"
              >
                <span class="k-minus text-[14px]" />
              </b-button>
            </b-input-group-prepend>
            <b-form-input
              v-model="data.item.layer"
              type="number"
              number
              min="1"
              placeholder="0"
              class="text-center"
              :formatter="formatNumber"
              @input="handleChangePackingLayerInput()"
            />
            <b-input-group-append>
              <b-button
                variant="outline-secondary"
                size="sm"
                class="!p-[6px] !bg-[#F8F8F8] !border-[#E2E2E2]"
                @click="handleChangePackingLayer(data.item, 'add')"
              >
                <span class="k-add text-[14px]" />
              </b-button>
            </b-input-group-append>
          </b-input-group>
        </div>
      </template>
      <template #custom-foot>
        <tr>
          <td
            colspan="3"
            class="!px-0 !py-[10px]"
          >
            <b-button
              size="lg"
              variant="ghost"
              class="!text-[14px] !text-[#F95031] !px-[4px] !font-normal !flex items-center justify-center gap-[4px]"
              :disabled="thereIsEmptyPackingMaterial"
              @click="addPackingMaterial"
            >
              <span class="k-add text-[18px]" /> Bahan Packing
            </b-button>
          </td>
        </tr>
      </template>
    </b-table>

    <div class="flex gap-[8px]">
      <b-button
        variant="outline-primary"
        class="!text-[14px] !w-full"
        @click="hide"
      >
        Kembali
      </b-button>
      <b-button
        :variant="isSaveButtonDisabled ? 'secondary' : 'primary'"
        class="!text-[14px] !w-full !flex items-center justify-center gap-2"
        :disabled="isSaveButtonDisabled"
        @click="handleSave"
      >
        <b-spinner
          v-if="isMutating"
          small
          variant="white"
        />
        {{ isMutating ? 'Menyimpan...' : 'Simpan' }}
      </b-button>
    </div>

  </b-modal>
</template>

<script>
import { mapState } from 'vuex'
import InfiniteSelect from '@/views/components/select/InfiniteSelect.vue'
import { fieldsModalPackingSettings } from '../config'
import TrashIcon from './TrashIcon.vue'

/**
   * Modal for updating the order packing material
   * @prop {Object} fetcher - The fetcher object for fetching packing materials
   * @returns {void}
   */
export default {
  name: 'ModalPackagingSettings',
  components: {
    InfiniteSelect,
    TrashIcon,
  },
  props: {
    fetcher: {
      type: Object,
      required: false,
      default: () => ({
        data: [],
        hasMore: false,
        loading: false,
      }),
    },
  },
  data() {
    return {
      position: undefined,
      selectedPackingMaterials: [],
      isMutating: false,
      defaultPackings: [],
      hasPackingLayerChanged: false,
      packingOptions: [],
      fields: fieldsModalPackingSettings,
      warehouseId: undefined,
      saveFn: undefined,
      beforeSaveHook: undefined,
    }
  },
  computed: {
    ...mapState('warehouse', ['profile']),
    thereIsOnlyOnePackingMaterial() {
      return this.selectedPackingMaterials.length === 1
    },
    thereIsEmptyPackingMaterial() {
      return this.selectedPackingMaterials.some(val => !val.name || !val.layer)
    },
    isSaveButtonDisabled() {
      return !this.hasPackingLayerChanged || this.thereIsEmptyPackingMaterial || this.isMutating
    },
  },
  created() {
    this.saveFn = undefined
  },
  methods: {
    isEmptyNewPacking(item) {
      const isNewItem = !item?.id
      const isEmpty = !item?.name || !item?.layer
      return isNewItem && isEmpty
    },
    isRemoveButtonDisabled(item) {
      return this.thereIsOnlyOnePackingMaterial || this.isEmptyNewPacking(item)
    },
    /**
       * Format the material options for the current packing to include the disabled state
       * @param currentPacking {Object} - The current packing
       * @returns {Array} - The material options with disabled state
       */
    getMaterialOptions(currentPacking) {
      return this.packingOptions.map(val => ({
        ...val,
        disabled: this.selectedPackingMaterials.some((selected, index) => selected.id === val.id && selected.id !== currentPacking.id),
      })) || []
    },
    /**
       * Exposed method to show the modal from the parent component
       * @param position {Number} - The position of the order
       * @param packings {Array} - The packings array
       * @param saveFn {Function} - Custom save handler
       * @param beforeSave {Function} - Hook to run before emitting on-save
       * @returns {void}
       */
    show({
      position, packings, saveFn, beforeSave,
    }) {
      this.position = position
      this.packingOptions = this.fetcher.data
      this.hasPackingLayerChanged = false
      this.saveFn = saveFn
      this.beforeSaveHook = beforeSave

      if (packings && packings.length > 0) {
        const formattedPackings = packings.map(val => ({
          name: val.name,
          layer: val.layer,
          id: val.id,
        }))
        this.defaultPackings = formattedPackings
        this.selectedPackingMaterials = _.cloneDeep(formattedPackings)
      } else {
        this.selectedPackingMaterials = [{
          id: 0,
          name: undefined,
          layer: undefined,
        }]
      }

      this.$refs.modalPackagingSettings.show()
    },
    /**
       * Exposed method to hide the modal from the parent component
       * @returns {void}
       */
    hide() {
      this.selectedPackingMaterials = []
      this.beforeSaveHook = undefined
      this.$refs.modalPackagingSettings.hide()
    },
    /**
       * Handle the process of saving the order packing material
       * @returns {void}
       */
    async handleSave() {
      this.isMutating = true
      try {
        const data = this.selectedPackingMaterials
        const payload = { position: this.position, data }

        await this.runBeforeSaveHook(payload)
        this.$emit('on-save', payload)
        this.hide()
      } catch (error) {
        const message = error?.message || 'Gagal mengubah bahan packing, silahkan coba lagi'
        this.$toast_error({ message })
      } finally {
        this.isMutating = false
      }
    },
    /**
       * Handle the process of searching for packing materials
       * @param search {String} - The search query
       * @returns {void}
       */
    async handleSearchPacking(search) {
      if (!search) {
        this.packingOptions = this.fetcher.data
        return
      }
      const searchResults = this.fetcher.data.filter(val => val.name.toLowerCase().includes(search.toLowerCase()))
      if (this.fetcher.hasMore || (searchResults.length === 0 && this.fetcher.hasMore)) {
        await this.fetcher.fetchData()
        this.handleSearchPacking(search)
        return
      }
      this.packingOptions = searchResults
    },
    handleIntersect() {
      if (this.fetcher && this.fetcher.fetchData) {
        this.fetcher.fetchData()
      }
    },
    addPackingMaterial() {
      this.selectedPackingMaterials.push({
        id: 0,
        name: undefined,
        layer: undefined,
      })
    },
    handleChangePackingLayerInput() {
      this.hasPackingLayerChanged = true
    },
    removePackingMaterial(index) {
      this.selectedPackingMaterials.splice(index, 1)
      this.hasPackingLayerChanged = true
      this.$emit('packing-materials-changed', this.selectedPackingMaterials)
    },
    isOnMinimumLayer(packing) {
      return packing.layer <= 1
    },
    handleChangePackingLayer(packing, type) {
      if (!packing) return
      let currentLayer = packing.layer || 0
      if (type === 'minus' && currentLayer > 1) currentLayer -= 1
      if (type === 'add') currentLayer += 1
      this.hasPackingLayerChanged = true
      this.$set(packing, 'layer', currentLayer)
    },
    /**
       * Reduce the packing material to the required format for the selection component
       * @param val {Object} - The packing material object
       * @returns {Object} - The reduced packing material object
       */
    reducePackingMaterial(val) {
      return {
        name: val.name,
        layer: val.layer,
        id: val.id,
      }
    },
    async runBeforeSaveHook(payload) {
      if (typeof this.beforeSaveHook === 'function') {
        await this.beforeSaveHook(payload)
      }
    },
    formatNumber(value) {
      return Number(value.replace(/[^0-9]/g, ''))
    },
  },
}
</script>

<style scoped>
  .c-packings {
    align-items: flex-center;
  }
  .c-select-qty {
    flex-direction: row;
    gap: 16px;
  }
  .c-qty {
    max-width: 120px;
  }
  @media (max-width: 470px) {
    .c-packings {
      align-items: flex-start;
    }
    .c-select-qty {
      flex-direction: column!important;
      gap: 8px;
    }
    .c-select-qty .v-select {
      width: 100%!important;
    }
    .c-qty {
      max-width: 100%;
    }
  }
</style>
